/**
 * SANCTALE scroll reveal — progressive enhancement.
 * Text stays visible until this script successfully enables reveals.
 */
(() => {
  const READY = 'sanctale-reveal-ready';
  const ATTR = 'data-sanctale-reveal';
  const BOUND = 'data-sanctale-reveal-bound';
  const REVEALED = 'is-revealed';
  const STAGGER_MS = 90;
  const BASE_DELAY_MS = 60;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  /** @type {IntersectionObserver | null} */
  let observer = null;
  let editorBound = false;

  const prefersReduced = () => reduceMotion.matches;

  const revealNow = (el) => {
    // Double rAF so browsers paint the hidden state before revealing.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.classList.add(REVEALED);
        // Drop compositing hints after the transition ends (Safari glyph ghosts).
        window.setTimeout(() => {
          el.style.willChange = 'auto';
        }, 900);
      });
    });
  };

  const prepare = (el) => {
    if (el.getAttribute(BOUND) === 'true') return;
    el.setAttribute(BOUND, 'true');

    const order = Number(el.getAttribute('data-sanctale-reveal-order') || '0');
    el.style.setProperty(
      '--sanctale-reveal-delay',
      `${BASE_DELAY_MS + Math.max(0, order) * STAGGER_MS}ms`
    );

    if (prefersReduced()) {
      revealNow(el);
      return;
    }

    if (observer) observer.observe(el);
  };

  const init = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(`[${ATTR}]`).forEach(prepare);
  };

  const ensureObserver = () => {
    if (observer || prefersReduced()) return;
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.15) return;
          observer.unobserve(entry.target);
          revealNow(entry.target);
        });
      },
      {
        threshold: [0, 0.15, 0.22],
        rootMargin: '0px 0px -6% 0px',
      }
    );
  };

  const bindEditor = () => {
    if (editorBound) return;
    editorBound = true;

    document.addEventListener('shopify:section:load', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      target.querySelectorAll(`[${ATTR}]`).forEach((el) => {
        el.removeAttribute(BOUND);
        el.classList.remove(REVEALED);
        if (observer) observer.unobserve(el);
      });
      if (prefersReduced()) {
        target.querySelectorAll(`[${ATTR}]`).forEach(revealNow);
        return;
      }
      ensureObserver();
      document.documentElement.classList.add(READY);
      init(target);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !observer) return;
      target.querySelectorAll(`[${ATTR}]`).forEach((el) => observer.unobserve(el));
    });
  };

  const enable = () => {
    bindEditor();

    if (prefersReduced()) {
      document.documentElement.classList.remove(READY);
      document.querySelectorAll(`[${ATTR}]`).forEach(revealNow);
      return;
    }

    ensureObserver();
    document.documentElement.classList.add(READY);
    init(document);
  };

  if (typeof reduceMotion.addEventListener === 'function') {
    reduceMotion.addEventListener('change', enable);
  } else if (typeof reduceMotion.addListener === 'function') {
    reduceMotion.addListener(enable);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enable, { once: true });
  } else {
    enable();
  }
})();
